import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} [dialog]
 * @property {HTMLElement} [layout]
 * @property {HTMLImageElement} [stageImage]
 * @property {HTMLElement} [thumbs]
 * @property {HTMLButtonElement} [thumbsPrev]
 * @property {HTMLButtonElement} [thumbsNext]
 * @property {HTMLButtonElement[]} [thumbButtons]
 * @property {HTMLElement} [status]
 */

/**
 * Masonry gallery with a full-screen lightbox.
 *
 * @extends {Component<Refs>}
 */
class GalleryComponent extends Component {
  /** @type {number} */
  #index = 0;

  /** @type {number} */
  #pointerStartX = 0;

  /** @type {boolean} */
  #pointerTracking = false;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { dialog, thumbs, stageImage } = this.refs;

    if (dialog instanceof HTMLDialogElement) {
      dialog.addEventListener('close', this.#handleDialogClose, { signal });
      dialog.addEventListener('keydown', this.#handleKeyDown, { signal });
    }

    if (thumbs instanceof HTMLElement) {
      thumbs.addEventListener('scroll', this.#updateThumbsControls, { signal, passive: true });
    }

    if (stageImage instanceof HTMLElement) {
      stageImage.addEventListener('pointerdown', this.#handlePointerDown, { signal });
      stageImage.addEventListener('pointerup', this.#handlePointerUp, { signal });
      stageImage.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    }

    window.addEventListener('resize', this.#updateThumbsControls, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#unlockScroll();
  }

  /**
   * Opens the lightbox at a given index.
   * @param {number} index
   * @param {Event} [event]
   */
  open(index, event) {
    event?.preventDefault();

    const { dialog } = this.refs;

    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#show(index);

    if (!dialog.open) {
      document.documentElement.setAttribute('scroll-lock', '');
      dialog.showModal();
    }

    requestAnimationFrame(() => {
      this.#updateThumbs(this.#index);
      this.#updateThumbsControls();
    });
  }

  close() {
    const { dialog } = this.refs;

    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    dialog.close();
  }

  previous() {
    this.#show(this.#index - 1);
  }

  next() {
    this.#show(this.#index + 1);
  }

  /**
   * Selects a thumbnail / image by index.
   * @param {number} index
   */
  select(index) {
    this.#show(index);
  }

  /**
   * Closes the lightbox when the cream background is clicked.
   * @param {MouseEvent} event
   */
  handleDialogClick(event) {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('button, img, .gallery-lightbox__thumbs, .gallery-lightbox__stage')) return;

    this.close();
  }

  scrollThumbsPrev() {
    this.#scrollThumbs(-1);
  }

  scrollThumbsNext() {
    this.#scrollThumbs(1);
  }

  /**
   * @param {number} index
   */
  #show(index) {
    const items = this.#items;
    const count = items.length;

    if (!count) return;

    const nextIndex = ((Number(index) % count) + count) % count;
    const item = items[nextIndex];

    if (!(item instanceof HTMLElement)) return;

    this.#index = nextIndex;
    this.#updateStage(item, nextIndex, count);
    this.#updateThumbs(nextIndex);
    this.#updateStatus(nextIndex, count);
  }

  /**
   * @param {HTMLElement} item
   * @param {number} index
   * @param {number} count
   */
  #updateStage(item, index, count) {
    const { stageImage } = this.refs;

    if (!(stageImage instanceof HTMLImageElement)) return;

    const src = item.dataset.fullUrl || '';
    const srcset = item.dataset.fullSrcset || '';
    const alt = item.dataset.alt || '';
    const width = Number(item.dataset.width);
    const height = Number(item.dataset.height);
    const instant = prefersReducedMotion();

    if (!instant && stageImage.src && stageImage.src !== src) {
      stageImage.classList.add('is-loading');
      stageImage.addEventListener(
        'load',
        () => {
          stageImage.classList.remove('is-loading');
        },
        { once: true }
      );
    } else {
      stageImage.classList.remove('is-loading');
    }

    stageImage.src = src;
    stageImage.srcset = srcset.trim();
    stageImage.alt = alt;
    stageImage.setAttribute('aria-label', `${index + 1} / ${count}`);

    if (width) stageImage.width = width;
    if (height) stageImage.height = height;
  }

  /**
   * @param {number} index
   */
  #updateThumbs(index) {
    const { thumbButtons } = this.refs;

    if (!Array.isArray(thumbButtons)) return;

    for (const [thumbIndex, button] of thumbButtons.entries()) {
      if (!(button instanceof HTMLElement)) continue;

      const isCurrent = thumbIndex === index;
      button.setAttribute('aria-current', isCurrent ? 'true' : 'false');

      if (isCurrent) {
        button.scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        });
      }
    }

    this.#updateThumbsControls();
  }

  /**
   * @param {number} index
   * @param {number} count
   */
  #updateStatus(index, count) {
    const { status } = this.refs;

    if (!(status instanceof HTMLElement)) return;

    const template = status.dataset.template || '';
    status.textContent = template.replaceAll('INDEX', String(index + 1)).replaceAll('COUNT', String(count));
  }

  #updateThumbsControls = () => {
    const { thumbs, thumbsPrev, thumbsNext } = this.refs;

    if (!(thumbs instanceof HTMLElement)) return;

    const maxScroll = thumbs.scrollWidth - thumbs.clientWidth;
    const canScroll = maxScroll > 8;
    const atStart = thumbs.scrollLeft <= 8;
    const atEnd = thumbs.scrollLeft >= maxScroll - 8;

    if (thumbsPrev instanceof HTMLButtonElement) {
      thumbsPrev.hidden = !canScroll || atStart;
    }

    if (thumbsNext instanceof HTMLButtonElement) {
      thumbsNext.hidden = !canScroll || atEnd;
    }
  };

  /**
   * @param {number} direction
   */
  #scrollThumbs(direction) {
    const { thumbs } = this.refs;

    if (!(thumbs instanceof HTMLElement)) return;

    thumbs.scrollBy({
      left: direction * thumbs.clientWidth * 0.75,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  #handleDialogClose = () => {
    this.#unlockScroll();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.previous();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
      case 'Home':
        event.preventDefault();
        this.#show(0);
        break;
      case 'End':
        event.preventDefault();
        this.#show(this.#items.length - 1);
        break;
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.#pointerTracking = true;
    this.#pointerStartX = event.clientX;
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (!this.#pointerTracking) return;

    this.#pointerTracking = false;

    const delta = event.clientX - this.#pointerStartX;

    if (Math.abs(delta) < 50) return;

    if (delta < 0) {
      this.next();
      return;
    }

    this.previous();
  };

  #unlockScroll() {
    document.documentElement.removeAttribute('scroll-lock');
  }

  get #items() {
    return Array.from(this.querySelectorAll('[data-gallery-item]'));
  }
}

if (!customElements.get('gallery-component')) {
  customElements.define('gallery-component', GalleryComponent);
}
